import { router, Stack } from 'expo-router';
import * as React from 'react';
import { View } from 'react-native';

import { ProfileEditScreen } from '@/components/profiles/profile-edit-screen';
import { useSession } from '@/components/session-provider';
import {
  normalizeProfileLinks,
  profileLinksToText,
  updateMyProfile,
  uploadProfileAvatar,
  type Profile,
} from '@/lib/member-profile';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

export default function MemberProfileEditRoute() {
  const { session, profile } = useSession();
  const userId = session?.user.id;
  const profilePhotosEnabled = useReleaseFeatureFlag('profilePhotos') === true;

  const [savedProfile, setSavedProfile] = React.useState<Profile | null>(null);
  const currentProfile = savedProfile ?? profile;
  const [fullNameInput, setFullName] = React.useState<string | null>(null);
  const [bioInput, setBio] = React.useState<string | null>(null);
  const [linksTextInput, setLinksText] = React.useState<string | null>(null);
  const [isSavingProfile, setIsSavingProfile] = React.useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const fullName = fullNameInput ?? currentProfile?.full_name ?? '';
  const bio = bioInput ?? currentProfile?.bio ?? '';
  const linksText = linksTextInput ?? profileLinksToText(currentProfile?.links ?? []);
  const phone = currentProfile?.phone ?? session?.user.phone ?? '';
  const phoneIsPublic = currentProfile?.phone_is_public ?? false;

  async function saveProfile() {
    if (!userId) {
      return;
    }

    setIsSavingProfile(true);
    setError(null);
    setMessage(null);
    try {
      const next = await updateMyProfile(userId, {
        bio: bio.trim() || null,
        fullName: fullName.trim() || null,
        links: normalizeProfileLinks(linksText),
        phone: phone.trim() || null,
        phoneIsPublic,
      });
      setSavedProfile(next);
      setFullName(next.full_name ?? '');
      setBio(next.bio ?? '');
      setLinksText(profileLinksToText(next.links));
      setMessage('Profile saved.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save profile.');
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function pickAvatar() {
    if (!userId) {
      return;
    }

    const ImagePicker = await import('expo-image-picker').catch(() => null);
    if (!ImagePicker) {
      setError(
        'Photo picker is unavailable in this build. Rebuild the development app and try again.'
      );
      return;
    }

    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError('Allow photo library access to update your profile photo.');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.88,
    });
    if (result.canceled) {
      return;
    }

    setIsUploadingAvatar(true);
    setError(null);
    setMessage(null);
    try {
      const avatarUrl = await uploadProfileAvatar(userId, result.assets[0]);
      setSavedProfile((current) => {
        const baseProfile = current ?? currentProfile;
        return baseProfile ? { ...baseProfile, avatar_url: avatarUrl } : baseProfile;
      });
      setMessage('Profile photo updated.');
    } catch (avatarError) {
      setError(avatarError instanceof Error ? avatarError.message : 'Could not update photo.');
    } finally {
      setIsUploadingAvatar(false);
    }
  }

  return (
    <View className="flex-1 bg-background">
      <Stack.Screen
        options={{ headerShown: process.env.EXPO_OS !== 'web', title: 'Edit profile' }}
      />
      <Stack.Screen.BackButton displayMode="minimal">Me</Stack.Screen.BackButton>
      <ProfileEditScreen
        error={error}
        form={{
          avatarButtonLabel: 'Edit photo',
          bio,
          bioPlaceholder: 'Morning miles, Bangkok heat, post-run coffee.',
          description: 'Edit what other runners see around the club.',
          isSaving: isSavingProfile,
          isUploadingAvatar,
          linksText,
          name: fullName,
          namePlaceholder: 'Mai Arun',
          onAvatarPress: profilePhotosEnabled ? () => void pickAvatar() : undefined,
          onBioChange: setBio,
          onLinksTextChange: setLinksText,
          onNameChange: setFullName,
          onSave: () => void saveProfile(),
          saveLabel: 'Save profile',
          title: 'Profile',
        }}
        message={message}
        onBack={() => router.back()}
        subtitle={session?.user.email}
        title="Edit profile"
        topPadding={24}
      />
    </View>
  );
}
